// src/utils/pinService.js
// Rapi + konsisten: meng-handle create/update umat-config dan helper cek record
import { apiFetch } from "../utils/wkCommon"; // attach JWT automatically
import { getUser } from "../utils/auth";
// Optional: jika Anda punya util encryptPin di src/utils/pin.ts
// import { encryptPin } from "./pin";

/**
 * Standard response:
 * { ok: boolean, message?: string, data?: any }
 */


/** helper: ambil nilai PIN dari entry Strapi (dukung beberapa shape) */
function extractStoredPin(entry) {
  if (!entry) return null;
  // shape v5 common: entry.attributes.PIN
  if (entry.attributes && (entry.attributes.PIN || entry.attributes.pin)) {
    return entry.attributes.PIN ?? entry.attributes.pin;
  }
  // shape alternatif: entry.PIN
  if (entry.PIN || entry.pin) return entry.PIN ?? entry.pin;
  // sometimes wrapped: entry.data?.attributes?.PIN
  if (entry.data && entry.data.attributes && (entry.data.attributes.PIN || entry.data.attributes.pin)) {
    return entry.data.attributes.PIN ?? entry.data.attributes.pin;
  }
  return null;
}

/** helper: dynamic-import encrypt function if tersedia, else fallback */
async function encryptValue(val) {
/*
  try {
    const mod = await import("../utils/pin").catch(() => null);
    const fn = mod?.encryptPin ?? mod?.default;
    if (typeof fn === "function") {
      // allow sync or promise-returning encrypt functions
      const out = fn(val);
      return out instanceof Promise ? await out : out;
    }
  } catch (e) {
    // ignore, fallback below
  }
*/
  // fallback (NOT secure long-term): base64
  return btoa(val);
}

/**
 * setPin(pin)
 * - Jika sudah ada umat-config untuk loginEmail -> update field PIN
 * - Jika belum ada -> create record umat-config dengan PIN
 */
export async function setPin(pin) {
  if (!pin || typeof pin !== "string") {
    return { ok: false, message: "PIN tidak valid" };
  }

  const user = getUser();
  if (!user) {
    return { ok: false, message: "Pengguna belum login" };
  }

  const loginEmail = user.LoginEmail ?? user.email;
  if (!loginEmail) {
    return { ok: false, message: "Email pengguna tidak tersedia" };
  }

  const encrypted = await encryptValue(pin);

  try {
    // 1) cari apakah sudah ada umat-config untuk loginEmail
    const q = encodeURIComponent(loginEmail);
    const listPath = `/api/umat-configs?filters%5BloginEmail%5D%5B$eq%5D=${q}&populate=*`;
    const listResp = await apiFetch(listPath);

    // Strapi v5 biasanya kembalikan { data: [...] }
    const arr = Array.isArray(listResp?.data) ? listResp.data : listResp;
    const existing = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;

    if (existing && (existing.documentId)) {
      const id = existing.documentId;
      await apiFetch(`/api/umat-configs/${id}`, {
        method: "PUT",
        body: { data: { PIN: encrypted, recordOwner: loginEmail, recordEditor: loginEmail } },
      });
      return { ok: true, message: "PIN berhasil diperbarui" };
    }

    // 2) buat record baru
    const createBody = {
      data: {
        loginEmail,
        umatID: user.umat?.umatID ?? null,
        PIN: encrypted,
        recordOwner: loginEmail,
        recordEditor: loginEmail,
      },
    };

    await apiFetch(`/api/umat-configs`, { method: "POST", body: createBody });
    return { ok: true, message: "PIN berhasil disimpan" };
  } catch (err) {
    // log agar mudah debugging (tapi jangan expose sensitive)
    console.error("[PINService.setPin] error:", err);

    // coba ambil pesan error dari response jika ada
    const msg =
      (err && (err.message || (err.error && err.error.message))) ||
      "Gagal menyimpan PIN. Periksa koneksi atau server.";
    return { ok: false, message: msg };
  }
}

/**
 * verifyPin(pin)
 * - Memanggil endpoint server yang memverifikasi PIN (asumsi ada endpoint)
 * - Jika server tidak sediakan endpoint ini, Anda bisa menghapus/ubah pemanggilan
 */
export async function verifyPin(pin) {
  if (!pin || typeof pin !== "string") {
    return { ok: false, message: "PIN tidak valid" };
  }

  const user = getUser();
  if (!user) return { ok: false, message: "Pengguna belum login" };

  const loginEmail = user.LoginEmail ?? user.email;
  if (!loginEmail) return { ok: false, message: "Email pengguna tidak tersedia" };

  const encryptedInput = await encryptValue(pin);

  try {
    const q = encodeURIComponent(loginEmail);
    const path = `/api/umat-configs?filters%5BloginEmail%5D%5B$eq%5D=${q}&populate=*`;
    const r = await apiFetch(path);

    const arr = Array.isArray(r?.data) ? r.data : Array.isArray(r) ? r : (r && r.data ? r.data : []);
    const existing = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;

    if (!existing) {
      return { ok: false, message: "Pengaturan PIN tidak ditemukan" };
    }

    const stored = extractStoredPin(existing);
    if (!stored) {
      return { ok: false, message: "PIN tersimpan tidak ada" };
    }

    // direct string compare (both encrypted the same way)
    if (stored === encryptedInput) {
      return { ok: true, message: "Verifikasi PIN berhasil", data: { matched: true } };
    }

    // tidak cocok
    return { ok: false, message: "PIN tidak cocok" };
  } catch (err) {
    console.error("[PINService.verifyPin] error:", err);
    const msg = (err && (err.message || (err.error && err.error.message))) || "Gagal memverifikasi PIN.";
    return { ok: false, message: msg };
  }
}

/**
 * hasPinRecord()
 * - Mengembalikan boolean apakah umat-config untuk user saat ini ada
 */
export async function hasPinRecord() {
  const user = getUser();
  if (!user) return false;

  const email = user.LoginEmail ?? user.email;
  if (!email) return false;

  try {
    const q = encodeURIComponent(email);
    const path = `/api/umat-configs?filters%5BloginEmail%5D%5B$eq%5D=${q}`;
    const r = await apiFetch(path);
    const arr = Array.isArray(r?.data) ? r.data : r;
    return Array.isArray(arr) && arr.length > 0;
  } catch (e) {
    console.warn("[PINService.hasPinRecord] fetch failed:", e);
    return false;
  }
}

export async function checkHasPin(emailParam = null) {
  const user = getUser();
  if (!user) return false;

  const email = emailParam || user.LoginEmail || user.email;
  if (!email) return false;

  try {
    const q = encodeURIComponent(email);
    const path = `/api/umat-configs?filters%5BloginEmail%5D%5B$eq%5D=${q}&populate=*`;
    const r = await apiFetch(path);

    const arr = Array.isArray(r?.data) ? r.data : r;
    const existing = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;

    if (!existing) return false;

    // 🔥 penting: cek PIN ada isi
    const pin =
      existing?.PIN ??
      null;

    return !!(pin && String(pin).trim() !== "");
  } catch (e) {
    console.warn("[PINService.hasPin] error:", e);
    return false;
  }
}

export async function resetPinByEmail(email) {
  if (!email) {
    return { ok: false, message: "Email tidak valid" };
  }

  try {
    const q = encodeURIComponent(email);

    // 1️⃣ cari umat-config berdasarkan email
    const listPath = `/api/umat-configs?filters%5BloginEmail%5D%5B$eq%5D=${q}&populate=*`;
    const listResp = await apiFetch(listPath);

    const arr = Array.isArray(listResp?.data) ? listResp.data : listResp;
    const existing = Array.isArray(arr) && arr.length > 0 ? arr[0] : null;

    if (!existing || !existing.documentId) {
      return { ok: false, message: "Data PIN tidak ditemukan" };
    }

    // 2️⃣ reset PIN (set null)
    const id = existing.documentId;

    await apiFetch(`/api/umat-configs/${id}`, {
      method: "PUT",
      body: {
        data: {
          PIN: null,
          recordEditor: email,
        },
      },
    });

    return { ok: true, message: "PIN berhasil direset" };

  } catch (err) {
    console.error("[resetPinByEmail] error:", err);

    const msg =
      err?.message ||
      "Gagal reset PIN. Periksa koneksi atau server.";

    return { ok: false, message: msg };
  }
}